// frontend/src/styles/theme.js
// ÚNICA FUENTE DE VERDAD PARA TODOS LOS ESTILOS - TEMAS CLARO Y OSCURO

// BASE DE TEMA (COMPARTIDO)
const baseTheme = {
  typography: {
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    fontSize: {
      xs: '0.62rem',
      sm: '0.78rem',
      md: '0.92rem',
      lg: '1.02rem',
      xl: '1.12rem',
      '2xl': '1.35rem'
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700
    }
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem'
  },
  borderRadius: {
    sm: '0.125rem',
    md: '0.25rem',
    lg: '0.375rem',
    xl: '0.5rem'
  },
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
  },
  transitions: {
    fast: '0.2s ease-in-out',
    medium: '0.3s ease-in-out',
    slow: '0.5s ease-in-out'
  },
  breakpoints: {
    xs: '480px',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px'
  },
  heights: {
    navbar: '44px',
    cintaOpciones: '48px',
    footer: '32px'
  },
  zIndex: {
    navbar: 1100,
    cintaOpciones: 45,
    modal: 9000,
    footer: 1000,
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 8990,
    popover: 1060,
    tooltip: 1070,
    // Capa para elementos flotantes (dropdowns de autocomplete, menús) que se montan
    // vía portal y deben verse POR ENCIMA de los modales (modal: 9000). Sin esto, un
    // desplegable abierto dentro de un modal queda tapado por el propio modal.
    dropdownOverModal: 9500
  }
};

// TEMA CLARO
export const lightTheme = {
  ...baseTheme,
  colors: {
  // kept for backward compatibility and repointed to the celeste (info) color
  celeste: '#17a2b8',
  // Alternate purple (nuevo) disponible como `violetaAlt`
  violeta: '#6f42c1',
  violetaHover: '#5a34a0',
  // Naranja para usos de alerta/acción secundaria
  naranja: '#ff6f00',
  naranjaHover: '#e65a00',
  naranjaBg: '#fff3e0',
  naranjaBorder: '#ffcc80',
  naranjaTxt: '#e65100',
  // Azul más fuerte para variantes de primary/acciones destacadas
  strongBlue: '#0d6efd',
  strongBlueHover: '#0b5ed7',
    amarillo: '#eed500',
    bgPrincipal: '#ffffff',
    bgSecundario: '#f8f9fa',
    bgModales: '#ffffff',
    bgModalesHead: '#f8f9fa',
    txtPrincipal: '#212529',
    txtSecondary: '#6c757d',
    txtSecundario: '#6c757d',
    txtInvalid: '#dc3545',
    borderDefault: '#dee2e6',
    borderValid: '#28a745',
    borderInvalid: '#dc3545',
    scrollTrack: '#f1f3f4',
    scrollThumb: '#c1c8cd',
    scrollThumbHover: '#a6b1ba',
  // shadow focus repointed to the celeste (info) color
  shadowFocus: 'rgba(23,162,184,0.25)',
  shadowPaper: 'rgba(0, 0, 0, 0.18)',
  shadowModal: '0 4px 16px rgba(0, 0, 0, 0.25)',
  // Color de sombra suave para controles flotantes (botones de mapa, etc.)
  shadowSoft: 'rgba(0, 0, 0, 0.25)',
  txtMuted: '#6b7280',
    surface: '#ffffff',
    surfaceHover: '#f8f9fa',
  // primary color unified to the celeste (info) tone used across the app
  primary: '#17a2b8',
  // Acento morado para acciones principales (como Guardar en legacy)
  accent: '#6f42c1',
  accentHover: '#5a34a0',
  primaryHover: '#138496',
  onPrimary: '#ffffff', // Color para elementos sobre fondo primario
    secondary: '#6c757d',
    secondaryHover: '#5a6268',
    success: '#28a745',
    successHover: '#218838',
    warning: '#ffc107',
    warningHover: '#e0a800',
    danger: '#dc3545',
    dangerHover: '#c82333',
    info: '#17a2b8',
    infoHover: '#138496',
    white: '#ffffff',
    transparent: 'transparent',
    inherit: 'inherit',
    overlayBackdrop: 'rgba(0, 0, 0, 0.5)',
    overlayBackdropSoft: 'rgba(0, 0, 0, 0.45)',
    overlayBackdropStrong: 'rgba(0, 0, 0, 0.8)',
    overlayBackdropLight: 'rgba(0, 0, 0, 0.22)',
    overlayOnPrimarySoft: 'rgba(255, 255, 255, 0.08)',
    overlayOnPrimaryBorder: 'rgba(255, 255, 255, 0.25)',
    warningBg: '#fff3cd',
    warningTxt: '#664d03',
    warningBorder: '#ffecb5',
    infoBg: '#d1ecf1',
    infoTxt: '#0c5460',
    infoBorder: '#bee5eb',
    successBg: '#d4edda',
    successTxt: '#155724',
    successBorder: '#c3e6cb',
    dangerBg: '#f8d7da',
    dangerTxt: '#721c24',
    dangerBorder: '#f5c6cb',
    // Trío violeta — mismo patrón que warning/info/success/danger arriba, para
    // el sello de "devolución" en Almacén (StampChip/Badge/Folio $tono="violeta").
    violetaBg: '#f1ecfb',
    violetaTxt: '#4a2d82',
    violetaBorder: '#d9c9f5',
    background: '#ffffff',
    text: '#212529',
    textSecondary: '#6c757d',
    border: '#dee2e6',
  borderLight: '#e9ecef',
  sectionBorder: '#c4ccd4',
  sectionOutline: 'rgba(0, 0, 0, 0.04)',
  // Zebra/hover para tablas
  rowAlternate: '#f3f5f7',
    hoverRow: '#eef2f7',
  // ── Tokens de gráficos (módulo Informes) ──
  chart1: '#17a2b8',
  chart2: '#6f42c1',
  chart3: '#28a745',
  chart4: '#ff6f00',
  chart5: '#0d6efd',
  chart6: '#d63384',
  chart7: '#20c997',
  chart8: '#6c757d',
  chartGrid: '#e9ecef',
  chartAxis: '#6c757d',
  chartTooltipBg: '#ffffff',
  chartTooltipBorder: '#dee2e6',
  // Variantes con opacidad para áreas/rellenos
  primaryAlpha: 'rgba(23, 162, 184, 0.15)',
  // Ítem resaltado con las flechas del teclado dentro de un desplegable.
  // Es más fuerte que surfaceHover (mouse) a propósito: cuando se navega con el
  // teclado, este resaltado es la única señal de qué se va a elegir con Enter,
  // así que tiene que ganarle al hover si el puntero quedó encima de otra fila.
  opcionActivaFondo: 'rgba(23, 162, 184, 0.28)',
  opcionActivaBorde: '#17a2b8',
  accentAlpha: 'rgba(111, 66, 193, 0.15)',
  successAlpha: 'rgba(40, 167, 69, 0.15)',
  infoAlpha: 'rgba(23, 162, 184, 0.15)',
  warningAlpha: 'rgba(255, 193, 7, 0.2)',
  dangerAlpha: 'rgba(220, 53, 69, 0.15)',
  // Estado apagado que NO es un error: una marca desactivada a propósito
  // (p. ej. un tenant que no se factura). Gris y no rojo justamente para que no
  // se lea como algo que hay que corregir.
  neutroAlpha: 'rgba(108, 117, 125, 0.15)',
  // ── Bordes y hover de badges/botones ghost translúcidos ──
  // Complementan los *Alpha de arriba (que son el fondo) con un borde más marcado
  // y, donde hace falta, un tono más fuerte para :hover. Antes esto se resolvía
  // concatenando `theme.colors.x + '22'` en el componente — prohibido por CLAUDE.md.
  primarySoftBorder: 'rgba(23, 162, 184, 0.28)',
  successSoftBorder: 'rgba(40, 167, 69, 0.35)',
  warningSoftBorder: 'rgba(255, 193, 7, 0.4)',
  dangerAlphaHover: 'rgba(220, 53, 69, 0.22)',
  naranjaAlpha: 'rgba(255, 111, 0, 0.15)',
  naranjaSoftBorder: 'rgba(255, 111, 0, 0.35)',
  naranjaAlphaHover: 'rgba(255, 111, 0, 0.22)',
  naranjaSoftBorderHover: 'rgba(255, 111, 0, 0.55)',
  amarilloAlpha: 'rgba(238, 213, 0, 0.14)',
  amarilloAlphaHover: 'rgba(238, 213, 0, 0.24)',
  // ── Halo de foco de un campo de formulario ──
  // El anillo que rodea al input activo. En rojo cuando el campo tiene un error,
  // para que el foco y la validación se lean como una sola señal.
  focusRingInfo: 'rgba(23, 162, 184, 0.2)',
  focusRingDanger: 'rgba(220, 53, 69, 0.2)',
  // ── Fondos informativos muy tenues ──
  // Seleccion: un ítem tildado dentro de una lista de opciones.
  // Subfila: la fila de detalle que cuelga de otra fila en una tabla.
  // Van bajísimos a propósito: tiñen sin competir con el texto que llevan encima.
  infoAlphaSeleccion: 'rgba(23, 162, 184, 0.09)',
  infoAlphaSubfila: 'rgba(23, 162, 184, 0.04)',
  // Lienzo de un código QR mostrado en pantalla para escanear con un teléfono.
  // Va siempre sobre blanco en los dos temas: en modo oscuro el contraste se
  // invierte y muchos lectores dejan de reconocer el código.
  qrLienzo: '#ffffff',
  // ── Tokens de impresión (papel siempre blanco, idénticos en ambos temas) ──
  printBg: '#ffffff',
  printText: '#000000',
  printBorder: '#000000',
  printBorderLight: '#cccccc',
  printBorderDashed: '#999999',
  printSectionBg: '#d1d5db',
  printTableHeaderBg: '#e5e7eb',
  printRowObsText: '#555555',
  printRowObsBg: '#f9f9f9',
  printLinedBg: '#e5e7eb',
  printEstadoGood: '#16a34a',
  printEstadoRegular: '#d97706',
  printEstadoBad: '#dc2626',
  printEstadoWarn: '#ea580c',
  printEstadoNeutral: '#6b7280',
  printResultadoOk: '#dcfce7',
  printResultadoCond: '#fef9c3',
  printFotoThumbBorder: '#cccccc',
  printMuted: '#777777',
  printBtnBack: '#374151',
  printBtnBlank: '#065f46',
  printBtnPrint: '#1a1a2e',
  // ── Tokens de badges oscuros fijos (checklist/portal QR: mismo aspecto en ambos temas) ──
  badgeGoodFg: '#22c55e',
  badgeGoodBg: '#14532d',
  badgeWarnFg: '#f59e0b',
  badgeWarnBg: '#451a03',
  badgeBadFg: '#ef4444',
  badgeBadBg: '#450a0a',
  badgeInfoFg: '#f97316',
  badgeInfoBg: '#431407',
  badgeNeutralFg: '#6b7280',
  badgeNeutralBg: '#1f2937',
  badgeIngresoFg: '#6ee7b7',
  badgeIngresoBg: '#064e3b',
  badgeEgresoFg: '#c7d2fe',
  badgeEgresoBg: '#312e81',
  badgeObsFg: '#f97316',
  badgeObsBg: '#1c1917',
  badgeObsBorder: '#92400e',
  badgeNoCtrlFg: '#9ca3af',
  badgeNoCtrlBg: '#1c1917',
  badgeNoCtrlBorder: '#374151',
  // Variante fg de warning para badge de resultado "condicional" (CheckList: PerfilEquipo)
  badgeWarnFg2: '#fdba74',
  // Azul de sección "Modificado" del changelog (más vívido que strongBlue)
  changelogChangedFg: '#3b82f6',
  // Borde para el badge de resultado "bueno" (CheckList: banner de resultado operativo)
  badgeGoodBorder: '#166534',
  // Botón "OK/posee" de check list (variante verde fija, igual en ambos temas)
  btnOkBg: '#065f46',
  btnOkFg: '#6ee7b7',
  btnOkBorder: '#059669',
  btnOkBgHover: '#047857',
  // Acentos de borde para tabs activos ingreso/egreso (CheckList)
  tabAccentIngreso: '#059669',
  tabAccentEgreso: '#6366f1',
  // ── Overlays translúcidos sobre foto/color (visor de fotos, headers de color) ──
  overlayOnPrimarySofter: 'rgba(255, 255, 255, 0.14)',
  overlayOnPrimarySofterHover: 'rgba(255, 255, 255, 0.26)',
  overlayOnDarkPhoto: 'rgba(14, 14, 14, 0.92)',
  overlayOnDarkPhotoSoft: 'rgba(0, 0, 0, 0.45)',
  overlayPhotoBackdrop: 'rgba(0, 0, 0, 0.96)',
  // Fondo sutil sobre superficie oscura fija (tabs inactivos, cajas de stats de check list)
  overlayOnDarkSubtle: 'rgba(255, 255, 255, 0.04)',
  // Estados intermedios de overlay claro sobre foto/color (toolbar del editor de anotaciones)
  overlayOnPrimaryMedium: 'rgba(255, 255, 255, 0.18)',
  overlayOnDarkMedium: 'rgba(255, 255, 255, 0.35)',
  overlayOnDarkStrong: 'rgba(255, 255, 255, 0.85)',
  iosPrimary: '#007AFF',
  // Paleta fija de anotación sobre fotos (marcador estilo iOS Markup, igual en ambos temas)
  markupPalette: ['#FF3B30', '#FFD60A', '#34C759', '#FFFFFF', '#1C1C1E'],
  // Trazo de contorno fijo para texto dibujado sobre foto (canvas de anotación, editor de fotos)
  canvasStrokeDefault: 'rgba(0, 0, 0, 0.55)',
  // Fondo oscuro fijo para previsualizar el logo de tema oscuro (ConfiguracionEmpresa), igual en ambos temas
  previewDarkBg: '#1a1a2e',
  // Fondo fijo del visor de cámara (EscanerQR), igual en ambos temas
  cameraBg: '#000000',
  // Efecto "spotlight" del recuadro de escaneo QR (EscanerQR), igual en ambos temas
  overlaySpotlight: 'rgba(0, 0, 0, 0.35)',
  // Borde más marcado que borderDefault para separadores fuertes (WorkspaceLayout, impresión)
  borderStrong: '#333333',
  // Color semilla inicial del selector de color custom (ConfiguracionBase: estados de reparación), igual en ambos temas
  brandDefaultColor: '#6366f1',
  // Verde agua para resaltar la fecha de "Mantenimiento iniciado" en listados (dato inmutable, ya calculado)
  tealAccent: '#0d9488',
  }
};
lightTheme.mapStyle = {
  // Paleta "Night Mode" oficial de Google Maps para featureType/stylers.
  // Es la misma en claro/oscuro: se usa solo cuando el usuario activa el mapa oscuro.
  darkGeometry: '#212121',
  darkLabelFill: '#757575',
  darkLabelStroke: '#212121',
  darkAdminGeometry: '#757575',
  darkAdminCountryLabelFill: '#9e9e9e',
  darkAdminLocalityLabelFill: '#bdbdbd',
  darkPoiLabelFill: '#757575',
  darkPoiParkGeometry: '#181818',
  darkPoiParkLabelFill: '#616161',
  darkPoiParkLabelStroke: '#1b1b1b',
  darkRoadGeometryFill: '#2c2c2c',
  darkRoadLabelFill: '#8a8a8a',
  darkRoadArterialGeometry: '#373737',
  darkRoadHighwayGeometry: '#3c3c3c',
  darkRoadHighwayControlledGeometry: '#4e4e4e',
  darkRoadLocalLabelFill: '#616161',
  darkTransitLabelFill: '#757575',
  darkWaterGeometry: '#000000',
  darkWaterLabelFill: '#3d3d3d',
};

// TEMA OSCURO
export const darkTheme = {
  ...baseTheme,
  colors: {
  // repointed to celeste (info) for backward compatibility
  celeste: '#17a2b8',
  // Alternate purple (nuevo) disponible como `violetaAlt`
  violeta: '#6f42c1',
  violetaHover: '#5a34a0',
  // Naranja para usos de alerta/acción secundaria
  naranja: '#ff6f00',
  naranjaHover: '#e65a00',
  naranjaBg: '#2d1a00',
  naranjaBorder: '#7a3800',
  naranjaTxt: '#ffb74d',
  // Azul más fuerte para variantes de primary/acciones destacadas
  strongBlue: '#0d6efd',
  strongBlueHover: '#0b5ed7',
    amarillo: '#eed500',
    bgPrincipal: '#1e1e1e',
    bgSecundario: '#292929',
    bgModales: '#212529',
    bgModalesHead: '#2a2a2a',
    txtPrincipal: '#e0e0e0',
    txtSecondary: '#bbb',
    txtSecundario: '#bbb',
    txtInvalid: '#f8d7da',
    borderDefault: '#555',
    borderValid: '#28a745',
    borderInvalid: '#dc3545',
    scrollTrack: '#2c2f33',
    scrollThumb: '#6c757d',
    scrollThumbHover: '#8e9ba5',
  // shadow focus repointed to celeste (info)
  shadowFocus: 'rgba(23,162,184,0.25)',
  shadowPaper: 'rgba(0, 0, 0, 0.55)',
  shadowModal: '0 4px 16px rgba(0, 0, 0, 0.45)',
  // Color de sombra suave para controles flotantes (botones de mapa, etc.)
  shadowSoft: 'rgba(0, 0, 0, 0.5)',
  txtMuted: '#9ca3af',
    surface: '#3a3a3a',
    surfaceHover: '#484848',
  // unify primary to celeste
  primary: '#17a2b8',
  // Acento morado para acciones principales (como Guardar en legacy)
  accent: '#6f42c1',
  accentHover: '#5a34a0',
  primaryHover: '#138496',
  onPrimary: '#ffffff', // Color para elementos sobre fondo primario
    secondary: '#6c757d',
    secondaryHover: '#5a6268',
    success: '#28a745',
    successHover: '#218838',
    warning: '#ffc107',
    warningHover: '#e0a800',
    danger: '#dc3545',
    dangerHover: '#c82333',
    info: '#17a2b8',
    infoHover: '#138496',
    white: '#ffffff',
    transparent: 'transparent',
    inherit: 'inherit',
    overlayBackdrop: 'rgba(0, 0, 0, 0.5)',
    overlayBackdropSoft: 'rgba(0, 0, 0, 0.45)',
    overlayBackdropStrong: 'rgba(0, 0, 0, 0.8)',
    overlayBackdropLight: 'rgba(0, 0, 0, 0.22)',
    overlayOnPrimarySoft: 'rgba(255, 255, 255, 0.08)',
    overlayOnPrimaryBorder: 'rgba(255, 255, 255, 0.25)',
    warningBg: '#3a2d0f',
    warningTxt: '#ffe69c',
    warningBorder: '#8f6b00',
    infoBg: '#0c2d35',
    infoTxt: '#7cc8d4',
    infoBorder: '#1a4f5a',
    successBg: '#0d2d17',
    successTxt: '#75b798',
    successBorder: '#1a4a28',
    dangerBg: '#2d0d12',
    dangerTxt: '#ea868f',
    dangerBorder: '#4a1a22',
    violetaBg: '#2a1d40',
    violetaTxt: '#c9a9f5',
    violetaBorder: '#5a3d8f',
    background: '#1e1e1e',
    text: '#e0e0e0',
    textSecondary: '#bbb',
    border: '#555',
  borderLight: '#666',
  sectionBorder: '#7a858f',
  sectionOutline: 'rgba(255, 255, 255, 0.05)',
  // Zebra/hover para tablas (un poco más claras que surface)
  rowAlternate: '#333333',
    hoverRow: '#3a3a3a',
  // ── Tokens de gráficos (módulo Informes) — versiones más luminosas para fondo oscuro ──
  chart1: '#3dd1e8',
  chart2: '#a78ae8',
  chart3: '#5dd879',
  chart4: '#ffa040',
  chart5: '#5c9eff',
  chart6: '#f06ba8',
  chart7: '#4fe3b6',
  chart8: '#9aa5ae',
  chartGrid: '#3a3a3a',
  chartAxis: '#9aa5ae',
  chartTooltipBg: '#323232',
  chartTooltipBorder: '#555555',
  // Variantes con opacidad para áreas/rellenos
  primaryAlpha: 'rgba(61, 209, 232, 0.22)',
  // Ver comentario en el tema claro: el resaltado por teclado debe ganarle al hover.
  opcionActivaFondo: 'rgba(61, 209, 232, 0.34)',
  opcionActivaBorde: '#3dd1e8',
  accentAlpha: 'rgba(167, 138, 232, 0.22)',
  successAlpha: 'rgba(93, 216, 121, 0.22)',
  infoAlpha: 'rgba(23, 162, 184, 0.22)',
  warningAlpha: 'rgba(255, 193, 7, 0.25)',
  dangerAlpha: 'rgba(220, 53, 69, 0.25)',
  // Ver comentario en el tema claro: estado apagado a propósito, no un error.
  neutroAlpha: 'rgba(187, 187, 187, 0.20)',
  // Ver comentario en el tema claro: bordes y hover de badges/botones ghost translúcidos.
  primarySoftBorder: 'rgba(61, 209, 232, 0.35)',
  successSoftBorder: 'rgba(93, 216, 121, 0.4)',
  warningSoftBorder: 'rgba(255, 193, 7, 0.45)',
  dangerAlphaHover: 'rgba(220, 53, 69, 0.3)',
  naranjaAlpha: 'rgba(255, 111, 0, 0.22)',
  naranjaSoftBorder: 'rgba(255, 111, 0, 0.42)',
  naranjaAlphaHover: 'rgba(255, 111, 0, 0.3)',
  naranjaSoftBorderHover: 'rgba(255, 111, 0, 0.6)',
  amarilloAlpha: 'rgba(238, 213, 0, 0.2)',
  amarilloAlphaHover: 'rgba(238, 213, 0, 0.3)',
  // Ver comentario en el tema claro: halo de foco y fondos informativos tenues.
  // Sobre fondo oscuro un velo al 4 % es invisible, así que van más marcados.
  focusRingInfo: 'rgba(23, 162, 184, 0.3)',
  focusRingDanger: 'rgba(220, 53, 69, 0.3)',
  infoAlphaSeleccion: 'rgba(23, 162, 184, 0.16)',
  infoAlphaSubfila: 'rgba(23, 162, 184, 0.08)',
  // Lienzo de un código QR mostrado en pantalla para escanear con un teléfono.
  // Va siempre sobre blanco en los dos temas: en modo oscuro el contraste se
  // invierte y muchos lectores dejan de reconocer el código.
  qrLienzo: '#ffffff',
  // ── Tokens de impresión (papel siempre blanco, idénticos en ambos temas) ──
  printBg: '#ffffff',
  printText: '#000000',
  printBorder: '#000000',
  printBorderLight: '#cccccc',
  printBorderDashed: '#999999',
  printSectionBg: '#d1d5db',
  printTableHeaderBg: '#e5e7eb',
  printRowObsText: '#555555',
  printRowObsBg: '#f9f9f9',
  printLinedBg: '#e5e7eb',
  printEstadoGood: '#16a34a',
  printEstadoRegular: '#d97706',
  printEstadoBad: '#dc2626',
  printEstadoWarn: '#ea580c',
  printEstadoNeutral: '#6b7280',
  printResultadoOk: '#dcfce7',
  printResultadoCond: '#fef9c3',
  printFotoThumbBorder: '#cccccc',
  printMuted: '#777777',
  printBtnBack: '#374151',
  printBtnBlank: '#065f46',
  printBtnPrint: '#1a1a2e',
  // ── Tokens de badges oscuros fijos (checklist/portal QR: mismo aspecto en ambos temas) ──
  badgeGoodFg: '#22c55e',
  badgeGoodBg: '#14532d',
  badgeWarnFg: '#f59e0b',
  badgeWarnBg: '#451a03',
  badgeBadFg: '#ef4444',
  badgeBadBg: '#450a0a',
  badgeInfoFg: '#f97316',
  badgeInfoBg: '#431407',
  badgeNeutralFg: '#6b7280',
  badgeNeutralBg: '#1f2937',
  badgeIngresoFg: '#6ee7b7',
  badgeIngresoBg: '#064e3b',
  badgeEgresoFg: '#c7d2fe',
  badgeEgresoBg: '#312e81',
  badgeObsFg: '#f97316',
  badgeObsBg: '#1c1917',
  badgeObsBorder: '#92400e',
  badgeNoCtrlFg: '#9ca3af',
  badgeNoCtrlBg: '#1c1917',
  badgeNoCtrlBorder: '#374151',
  // Variante fg de warning para badge de resultado "condicional" (CheckList: PerfilEquipo)
  badgeWarnFg2: '#fdba74',
  // Azul de sección "Modificado" del changelog (más vívido que strongBlue)
  changelogChangedFg: '#3b82f6',
  // Borde para el badge de resultado "bueno" (CheckList: banner de resultado operativo)
  badgeGoodBorder: '#166534',
  // Botón "OK/posee" de check list (variante verde fija, igual en ambos temas)
  btnOkBg: '#065f46',
  btnOkFg: '#6ee7b7',
  btnOkBorder: '#059669',
  btnOkBgHover: '#047857',
  // Acentos de borde para tabs activos ingreso/egreso (CheckList)
  tabAccentIngreso: '#059669',
  tabAccentEgreso: '#6366f1',
  // ── Overlays translúcidos sobre foto/color (visor de fotos, headers de color) ──
  overlayOnPrimarySofter: 'rgba(255, 255, 255, 0.14)',
  overlayOnPrimarySofterHover: 'rgba(255, 255, 255, 0.26)',
  overlayOnDarkPhoto: 'rgba(14, 14, 14, 0.92)',
  overlayOnDarkPhotoSoft: 'rgba(0, 0, 0, 0.45)',
  overlayPhotoBackdrop: 'rgba(0, 0, 0, 0.96)',
  // Fondo sutil sobre superficie oscura fija (tabs inactivos, cajas de stats de check list)
  overlayOnDarkSubtle: 'rgba(255, 255, 255, 0.04)',
  // Estados intermedios de overlay claro sobre foto/color (toolbar del editor de anotaciones)
  overlayOnPrimaryMedium: 'rgba(255, 255, 255, 0.18)',
  overlayOnDarkMedium: 'rgba(255, 255, 255, 0.35)',
  overlayOnDarkStrong: 'rgba(255, 255, 255, 0.85)',
  iosPrimary: '#007AFF',
  // Paleta fija de anotación sobre fotos (marcador estilo iOS Markup, igual en ambos temas)
  markupPalette: ['#FF3B30', '#FFD60A', '#34C759', '#FFFFFF', '#1C1C1E'],
  // Trazo de contorno fijo para texto dibujado sobre foto (canvas de anotación, editor de fotos)
  canvasStrokeDefault: 'rgba(0, 0, 0, 0.55)',
  // Fondo oscuro fijo para previsualizar el logo de tema oscuro (ConfiguracionEmpresa), igual en ambos temas
  previewDarkBg: '#1a1a2e',
  // Fondo fijo del visor de cámara (EscanerQR), igual en ambos temas
  cameraBg: '#000000',
  // Efecto "spotlight" del recuadro de escaneo QR (EscanerQR), igual en ambos temas
  overlaySpotlight: 'rgba(0, 0, 0, 0.35)',
  // Borde más marcado que borderDefault para separadores fuertes (WorkspaceLayout, impresión) — más claro en oscuro para mantener contraste
  borderStrong: '#a0a0a0',
  // Color semilla inicial del selector de color custom (ConfiguracionBase: estados de reparación), igual en ambos temas
  brandDefaultColor: '#6366f1',
  // Verde agua para resaltar la fecha de "Mantenimiento iniciado" en listados (dato inmutable, ya calculado)
  tealAccent: '#2dd4bf',
  }
};

darkTheme.mapStyle = lightTheme.mapStyle;
